import { ClinicalDataQuestion } from '@brands/services/clinical_data/types/types';
import React from 'react';
import { FieldValues, Path, PathValue, UseFormSetValue, UseFormTrigger } from 'react-hook-form';

import styles from './styles.module.scss';

interface QuestionAndRadioButtonProps<T extends FieldValues> {
  element: ClinicalDataQuestion;
  value: T[keyof T];
  setValue: UseFormSetValue<T>;
  trigger: UseFormTrigger<T>;
  isMedicalHistory?: boolean;
}

const QuestionAndRadioButton = <T extends FieldValues>({
  element,
  value,
  setValue,
  trigger,
  isMedicalHistory = false,
}: QuestionAndRadioButtonProps<T>): JSX.Element => {
  const isSelected = !!value;

  const handleClick = (): void => {
    const nextValue = !isSelected;

    const safeValue = value ?? {};

    if ((element.id === 'none_of_the_above' || element.id === 'medical_condition_none_of_the_above') && nextValue) {
      Object.keys(safeValue).forEach((key) => {
        if (key !== 'none_of_the_above' && key !== 'medical_condition_none_of_the_above') {
          setValue(key as Path<T>, false as PathValue<T, Path<T>>, { shouldValidate: true });
        }
      });

      setValue(element.id as Path<T>, true as PathValue<T, Path<T>>, { shouldValidate: true });
    } else {
      if (nextValue) {
        setValue(
          isMedicalHistory ? ('medical_condition_none_of_the_above' as Path<T>) : ('none_of_the_above' as Path<T>),
          false as PathValue<T, Path<T>>,
          { shouldValidate: true },
        );
      }

      setValue(element.id as Path<T>, nextValue as PathValue<T, Path<T>>, { shouldValidate: true });
    }

    trigger();
  };

  const handleCustomEntry = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    // If the user presses Enter, we want to set the value and trigger validation
    if (e.key === 'Enter') {
      e.preventDefault();
      const val = e.currentTarget.value.trim();
      if (!val) return;

      setValue(element.id as Path<T>, val as PathValue<T, Path<T>>, { shouldValidate: true });
      trigger(); // ✅ add this line to revalidate immediately
    }
  };

  return (
    <div
      className={`${styles.row} row fs-exclude ${styles.medicalFormRow} ${isSelected ? styles.selectClass : ''}`}
      onClick={handleClick}
    >
      <div className={styles.questionAndButtonContainer}>
        <div className={`col-6 col-sm-3 px-0 ${styles.select}`}>
          <div className={styles.check}>
            <input
              className={`form-check-input fs-exclude ${styles.checkInput} mt-0 ${
                isSelected ? styles.primaryColor : ''
              }`}
              data-testid={`medical-history-question-${element.id}`}
              type="checkbox"
              name={element.id}
              id={element.id}
              checked={isSelected}
              readOnly
            />
          </div>
        </div>
        <div className={`${styles.question} col-12 col-sm-8 px-0`}>
          <div className={`${styles.label} fs-unmask`}>
            {element.type === 'textarea' && isSelected ? (
              <div className={styles.selectSelf} onClick={(e) => e.stopPropagation()}>
                <div className={`fs-exclude ${styles.textField}`}>
                  <input
                    name={element.descriptionId || ''}
                    type="text"
                    data-testid={`medical-history-question-${element.id}`}
                    className={`fs-exclude ${typeof value === 'string' && value.length > 32 ? styles.placeholder : ''}`}
                    value={typeof value === 'string' ? value : ''}
                    onChange={(e) => {
                      setValue(element.id as Path<T>, e.target.value as PathValue<T, Path<T>>, {
                        shouldValidate: true,
                      });
                      trigger(); // ✅ add this line to revalidate immediately
                    }}
                    onKeyDown={handleCustomEntry}
                  />
                </div>
              </div>
            ) : (
              element.title
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuestionAndRadioButton;
